// Pull drawing + text primitives out of a pdf.js page, in PDF user space.
//
// pdf.js reports path bounding boxes in the path's *local* space, with the
// placement carried by preceding `transform` operators, so the CTM stack has to
// be tracked or every rect lands at the origin.

import {
  IDENTITY,
  transform,
  transformBox,
  matrixScale,
  boxWidth,
  boxHeight,
} from './geometry.js';

// Subpath command encoding used inside OPS.constructPath arguments.
const DRAW_MOVE_TO = 0;
const DRAW_LINE_TO = 1;
const DRAW_CURVE_TO = 2;
const DRAW_QUADRATIC_CURVE_TO = 3;
const DRAW_CLOSE_PATH = 4;
const DRAW_ARG_COUNT = {
  [DRAW_MOVE_TO]: 2,
  [DRAW_LINE_TO]: 2,
  [DRAW_CURVE_TO]: 6,
  [DRAW_QUADRATIC_CURVE_TO]: 4,
  [DRAW_CLOSE_PATH]: 0,
};

/**
 * Split a flat DrawOPS array into subpaths, each with its own bounding box and
 * a note of whether it was built purely from straight segments.
 */
function decodeSubpaths(data) {
  const subpaths = [];
  let current = null;

  const start = (x, y) => {
    current = {
      x0: x, y0: y, x1: x, y1: y,
      points: 1,
      lines: 0,
      curves: 0,
      closed: false,
      pts: [[x, y]],
    };
    subpaths.push(current);
  };
  const extend = (x, y) => {
    if (!current) start(x, y);
    current.x0 = Math.min(current.x0, x);
    current.y0 = Math.min(current.y0, y);
    current.x1 = Math.max(current.x1, x);
    current.y1 = Math.max(current.y1, y);
    current.points += 1;
    current.pts.push([x, y]);
  };

  for (let i = 0; i < data.length; ) {
    const op = data[i++];
    const argc = DRAW_ARG_COUNT[op];
    if (argc === undefined) break; // unknown opcode: bail rather than misread
    if (op === DRAW_MOVE_TO) {
      start(data[i], data[i + 1]);
    } else if (op === DRAW_LINE_TO) {
      extend(data[i], data[i + 1]);
      if (current) current.lines += 1;
    } else if (op === DRAW_CURVE_TO || op === DRAW_QUADRATIC_CURVE_TO) {
      // Control points overestimate the hull slightly; fine for classification.
      for (let k = 0; k < argc; k += 2) extend(data[i + k], data[i + k + 1]);
      if (current) current.curves += 1;
    } else if (op === DRAW_CLOSE_PATH) {
      if (current) current.closed = true;
    }
    i += argc;
  }
  return subpaths;
}

/**
 * Is this subpath box-shaped? Checkbox squares are very often drawn with
 * rounded or chamfered corners, so demanding perfectly straight segments misses
 * them. Straight edges must dominate, which still excludes circles and ellipses
 * (all curve, no lines) that would otherwise pass a square bounding-box test.
 */
function isRectangular(sub) {
  return sub.lines >= 3 && sub.curves <= sub.lines;
}

/**
 * How much of a subpath sits near the edge of its own bounding box. An outline
 * that traces a frame scores ~1; a filled silhouette or a decorative squiggle
 * has interior detail and scores well below it. This is what lets a deliberately
 * wobbly, hand-drawn box be recognised as a box at all — it is nearly all curve
 * segments, so no straight-edge test will find it.
 */
function hugFraction(sub, box) {
  const w = box.x1 - box.x0;
  const h = box.y1 - box.y0;
  if (w <= 0 || h <= 0 || sub.pts.length === 0) return 0;
  const margin = Math.min(w, h) * 0.12;
  let near = 0;
  for (const [x, y] of sub.pts) {
    if (
      x - box.x0 <= margin ||
      box.x1 - x <= margin ||
      y - box.y0 <= margin ||
      box.y1 - y <= margin
    ) {
      near += 1;
    }
  }
  return near / sub.pts.length;
}

const boxArea = (b) => Math.max(0, b.x1 - b.x0) * Math.max(0, b.y1 - b.y0);

function strictlyInside(outer, inner) {
  return (
    inner.x0 > outer.x0 - 0.5 &&
    inner.y0 > outer.y0 - 0.5 &&
    inner.x1 < outer.x1 + 0.5 &&
    inner.y1 < outer.y1 + 0.5 &&
    inner.x1 - inner.x0 < outer.x1 - outer.x0 &&
    inner.y1 - inner.y0 < outer.y1 - outer.y0
  );
}

/**
 * Turn edge-hugging outlines into frames.
 *
 * A hand-drawn box is painted as a *filled ring* — ink only between an outer and
 * an inner contour — so judging by the paint operator marks it solid and the
 * empty middle, the part you write in, is never offered as a field. These are
 * reported as unfilled boxes instead.
 *
 * Only shapes that already failed the rectangle test arrive here, so a plainly
 * drawn solid rectangle never reaches this path; what is left is curved outlines
 * that trace the rim of their own box. A ring's contours arrive separately and
 * overlap almost exactly, which candidate de-duplication collapses later.
 */
function framesFromOutlines(outlines, pageBox) {
  const limit = boxArea(pageBox) * 0.4;
  const sized = outlines.filter((o) => boxArea(o.box) <= limit);

  return sized
    // An outline enclosing another one is a grouping — the flourish drawn
    // around a whole row of stat boxes, or the outer rim of a ring. Keeping it
    // would swallow the individual boxes during de-duplication, so the innermost
    // shape wins and the writable area is what survives.
    .filter((o) => !sized.some((other) => other !== o && strictlyInside(o.box, other.box)))
    .map((o) => ({ box: o.box, filled: false, thickness: 1, frame: true }));
}

/**
 * Extract line, rectangle and text primitives from one page.
 *
 * @param {object} page pdf.js PDFPageProxy
 * @returns {Promise<object>} primitives in PDF user space
 */
export async function extractPrimitives(page, OPS) {
  const opList = await page.getOperatorList();
  const viewport = page.getViewport({ scale: 1, rotation: 0 });
  const [px0, py0, px1, py1] = viewport.viewBox;
  const pageBox = { x0: px0, y0: py0, x1: px1, y1: py1 };

  const lines = [];
  const rects = [];
  const outlines = [];
  let imageCoverage = 0;

  let ctm = IDENTITY;
  const stack = [];
  let lineWidth = 1;

  for (let i = 0; i < opList.fnArray.length; i++) {
    const fn = opList.fnArray[i];
    const args = opList.argsArray[i];

    if (fn === OPS.save) {
      stack.push({ ctm, lineWidth });
    } else if (fn === OPS.restore) {
      const prev = stack.pop();
      if (prev) ({ ctm, lineWidth } = prev);
    } else if (fn === OPS.transform) {
      ctm = transform(ctm, args);
    } else if (fn === OPS.setLineWidth) {
      lineWidth = args[0];
    } else if (fn === OPS.paintImageXObject || fn === OPS.paintInlineImageXObject) {
      const box = transformBox({ x0: 0, y0: 0, x1: 1, y1: 1 }, ctm);
      imageCoverage += Math.abs(boxWidth(box) * boxHeight(box));
    } else if (fn === OPS.constructPath) {
      const [drawOp, paths, minMax] = args;
      const filled =
        drawOp === OPS.fill ||
        drawOp === OPS.eoFill ||
        drawOp === OPS.fillStroke ||
        drawOp === OPS.eoFillStroke ||
        drawOp === OPS.closeFillStroke ||
        drawOp === OPS.closeEOFillStroke;

      // `paths` is an array of flat DrawOPS arrays (one per constructPath run).
      const runs = Array.isArray(paths) ? paths : [paths];
      let decoded = [];
      for (const run of runs) {
        if (!run || typeof run.length !== 'number') continue;
        decoded = decoded.concat(decodeSubpaths(run));
      }
      // If decoding produced nothing usable, fall back to the reported bounds.
      if (decoded.length === 0 && Array.isArray(minMax) && minMax.length >= 4) {
        decoded = [
          {
            x0: Math.min(minMax[0], minMax[2]),
            y0: Math.min(minMax[1], minMax[3]),
            x1: Math.max(minMax[0], minMax[2]),
            y1: Math.max(minMax[1], minMax[3]),
            points: 2,
            straight: true,
            closed: false,
          },
        ];
      }

      const strokeWidth = lineWidth * matrixScale(ctm);
      for (const sub of decoded) {
        const box = transformBox(sub, ctm);
        const w = boxWidth(box);
        const h = boxHeight(box);
        if (!Number.isFinite(w) || !Number.isFinite(h)) continue;

        const thickness = Math.max(strokeWidth, 0.1);
        // A "line" is a run that collapses in one axis. Filled thin rects (very
        // common for rules) collapse the same way, so they land here too.
        const thinV = h <= Math.max(thickness * 2, 2.5);
        const thinH = w <= Math.max(thickness * 2, 2.5);

        if (thinV && w >= 8 && !thinH) {
          lines.push({ box, orientation: 'h', length: w, thickness, filled });
        } else if (thinH && h >= 8 && !thinV) {
          lines.push({ box, orientation: 'v', length: h, thickness, filled });
        } else if (w >= 3 && h >= 3 && isRectangular(sub)) {
          rects.push({ box, filled, thickness });
        } else if (w >= 20 && h >= 10 && sub.pts && hugFraction(sub, box) >= 0.9) {
          // Not straight-edged enough to be a rectangle, but it traces the rim
          // of its own box. Held back until the whole page is read, because
          // only a nested pair proves the middle is hollow.
          outlines.push({ box });
        }
      }
    }
  }

  rects.push(...framesFromOutlines(outlines, pageBox));

  // Runs after getOperatorList above, which is what populates commonObjs with
  // the resolved font objects that carry real font names.
  const text = await extractText(page);

  return {
    pageBox,
    rotation: page.rotate || 0,
    lines,
    rects,
    text,
    // Scanned pages are essentially one big image and yield no useful geometry.
    imageDominant:
      imageCoverage > 0.6 * Math.abs((px1 - px0) * (py1 - py0)) && text.length < 5,
  };
}

/** Text items with user-space boxes, sorted into reading order. */
async function extractText(page) {
  const content = await page.getTextContent({ includeMarkedContent: false });
  const styles = content.styles || {};
  const items = [];
  for (const item of content.items) {
    if (typeof item.str !== 'string' || item.str.length === 0) continue;
    // pdf.js synthesises whitespace-only items to represent the gaps between
    // text runs. They cover real estate but contain no ink, so counting them as
    // content makes blanks look occupied.
    if (item.str.trim().length === 0) continue;
    const m = item.transform;
    const x = m[4];
    const y = m[5];
    const height = item.height || Math.hypot(m[2], m[3]) || 10;
    const width = item.width || 0;
    if (width <= 0) continue;
    items.push({
      str: item.str,
      box: { x0: x, y0: y, x1: x + width, y1: y + height },
      fontHeight: height,
      // Per-character advance, used to locate underscore runs inside an item.
      charWidth: width / item.str.length,
      // Symbol fonts (Wingdings and friends) draw ordinary letters as boxes, so
      // the real font name is needed to tell a checkbox from the letter "q".
      // `styles` only reports a generic CSS family ("sans-serif"), so the
      // actual name has to come from the resolved font object.
      fontFamily: styles[item.fontName]?.fontFamily || '',
      fontName: resolveFontName(page, item.fontName),
    });
  }
  items.sort((a, b) => b.box.y0 - a.box.y0 || a.box.x0 - b.box.x0);
  return items;
}

/**
 * Real font name (e.g. "ZapfDingbats", "ABCDEF+Wingdings") for a pdf.js internal
 * font id. Returns '' when the font object has not been resolved, which is not
 * an error — it only means symbol-font checkbox detection is unavailable.
 */
function resolveFontName(page, fontId) {
  if (!fontId || !page.commonObjs) return '';
  try {
    if (typeof page.commonObjs.has === 'function' && !page.commonObjs.has(fontId)) return '';
    return page.commonObjs.get(fontId)?.name || '';
  } catch {
    return '';
  }
}
